import {
  AfterViewInit,
  Component,
  OnChanges,
  OnDestroy,
  viewChild
} from '@angular/core';
import { MatOption, MatOptionModule } from '@angular/material/core';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { debounceTime, delay, finalize, map, takeUntil, tap } from 'rxjs';

import { CommonModule } from '@angular/common';
import { BaseFormKitModule } from '../base/base-form-kit.module';
import { BaseSelectSearchProperties } from './base/base-select-search-properties.component';

@Component({
    selector: 'select-search',
    imports: [
        CommonModule,
        MatSelectModule,
        NgxMatSelectSearchModule,
        MatOptionModule,
        BaseFormKitModule,
    ],
    styleUrls: ['../base/base-form-kit.scss', './select-search.component.scss'],
    providers: [NgxMatSelectSearchModule],
    templateUrl: './select-search.component.html'
})
export class SelectSearchComponent
  extends BaseSelectSearchProperties
  implements OnDestroy, OnChanges, AfterViewInit
{
  selectedItem: any = {};
  readonly selectElement = viewChild<MatSelect>('matSelect');
  isAddTooltipToMatSelect: boolean = false;

  override ngOnInit() {
    super.ngOnInit();
    if (this.value === -1 || this.value === undefined || this.value === '')
      this.value = null;
  }

  ngAfterViewInit(): void {
    if (!this.placeholder)
      this.placeholder = this.optimalSearch
        ? 'messages.OptimalSearch'
        : 'common.search';
  }

  override ngOnChanges(changes: any): void {
    try {
      if (changes.isDisabled) this.handleEnableStateControl();
      
      if (this.value === -1 || this.value === undefined || this.value === '')
        this.value = null;

      if (changes?.items && this.isFetchData) {
        if (this.items && this.items?.length) this.setFilteredItems();
        else {
          this.items = [];
          this.filteredItems.next([]);
          this.emitValue(null);
          this.emitItem(null, null);
        }
      }
    } catch (error) {
      console.error('SelectSearch Error => ', error);
    }
  }

  setFilteredItems() {
    try {
      this.initLoading = false;

      // Default items optimal search for display
      if (this.optimalSearch) this.filteredItems.next(this.items?.slice(0, 10));
      // Normal search
      else this.filteredItems.next(this.items?.slice(0, this.items?.length));

      this.filterControl.valueChanges
        .pipe(
          tap(() => (this.loading = true)),
          takeUntil(this.onDestroy$),
          // Optimal search with delay
          debounceTime(this.optimalSearch ? 200 : 0),
          map((searchText) => (searchText as string)?.toLowerCase()?.trim()),
          map((searchText) => {
            if (!this.items) return [];

            if (!searchText) {
              // The optimal search returns the first 10 items
              if (this.optimalSearch) this.items?.slice(0, 10);
              // Normal search returns all items
              else this.items?.slice(0, this.items?.length);
            }

            // Check at least 3 letters for optimal search
            if (searchText.length < 3 && this.optimalSearch) {
              if (this.selectedItem) {
                let items = this.items?.slice(0, 10);

                const isItemExists = items?.some(
                  (item) => item.id === this.selectedItem.id
                );
                if (!isItemExists) {
                  items = this.items?.slice(0, 9);
                  items?.push(this.selectedItem);
                  return items;
                }
              }
              return this.items?.slice(0, 10);
            }

            // filter
            return this.items?.filter(
              (item) =>
                item[this.optionTitle]
                  ?.toString()
                  ?.toLowerCase()
                  ?.indexOf(searchText?.toLowerCase()) > -1
            );
          }),
          delay(this.optimalSearch ? 400 : 0),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe((filteredItems) => {
          this.loading = false;
          this.filteredItems.next(filteredItems);
        });

      this.onCheckAddTooltipToSelectSearch(this.selectElement());
    } catch (error) {
      console.error('SelectSearch Error => ', error);
    }
  }

  //#region Option Tooltip

  onCheckAddTooltipToOption(event: MatOption): boolean {
    try {
      if (!event) return false;

      const optionElement = document.getElementById(event?.id);
      const titleElement = optionElement?.querySelector('.title');

      if (!titleElement || !optionElement) return false;

      const optionElementWidth = +optionElement?.offsetWidth;
      const titleElementWidth = +(titleElement as HTMLElement)?.offsetWidth;
      const result = optionElementWidth - titleElementWidth;

      if (result <= 20) return true;
      else return false;
    } catch (error) {
      console.error('SelectSearch Error => ', error);
      return false;
    }
  }

  onCheckAddTooltipToSelectSearch(event: MatSelect) {
    try {
      if (!event) return;

      if (!this.selectedItem) return;

      const timeOut = setTimeout(() => {
        const selectElement = document.getElementById(event?.id);
        const optionElement = selectElement?.querySelector('.mat-select-value');
        const titleElement = selectElement?.querySelector(
          '.mat-select-min-line'
        );

        if (!titleElement || !selectElement) return;

        const optionElementWidth = +(optionElement as HTMLElement)?.offsetWidth;
        const titleElementWidth = +(titleElement as HTMLElement)?.offsetWidth;

        const result = optionElementWidth - titleElementWidth;

        if (result <= 50) this.isAddTooltipToMatSelect = true;
        else this.isAddTooltipToMatSelect = false;

        clearTimeout(timeOut);
      }, 300);
    } catch (error) {
      console.error('SelectSearch Error => ', error);
    }
  }

  //#endregion

  clear() {
    this.value = null;
    this.itemChange.emit(null);
    this.valueChange.emit(null);
  }

  refresh() {
    this.value = null;
    this.itemChange.emit(null);
    this.valueChange.emit(null);
    this.onRefresh.emit();
  }

  getSelectedItemTitle() {
    if (this.selectedItem) return this.selectedItem[this.optionTitle];
    else return '';
  }

  //#region emit

  emitItem(item, event) {
    if (event?.isUserInput) {
      this.selectedItem = item;
      this.itemChange.emit(item);
      this.onCheckAddTooltipToSelectSearch(this.selectElement());
    }
  }

  override emitValue(value) {
    this.valueChange.emit(value);
    if (this.isDeleteAfterEmit) {
      const timeOut = setTimeout(() => {
        this.clear();
        clearTimeout(timeOut);
      }, 100);
    }
  }

  //#endregion
}
